import * as React from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "../../lib/utils"

// Simplified Accordion Context
const AccordionContext = React.createContext<{
  expanded: string | undefined;
  setExpanded: (value: string | undefined) => void;
}>({ expanded: undefined, setExpanded: () => {} });

const Accordion = ({ children, type: _type = "single", collapsible = true, className, ...props }: any) => {
  const [expanded, setExpandedState] = React.useState<string | undefined>(undefined);

  const setExpanded = (value: string | undefined) => {
    if (collapsible && value === expanded) {
      setExpandedState(undefined);
    } else {
      setExpandedState(value);
    }
  };

  return (
    <AccordionContext.Provider value={{ expanded, setExpanded }}>
      <div className={cn("", className)} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

const AccordionItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { value: string }
>(({ className, value, ...props }, ref) => (
  <div ref={ref} className={cn("border-b", className)} data-value={value} {...props} />
))
AccordionItem.displayName = "AccordionItem"

const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, ...props }, ref) => {
  return (
    <div className="flex">
      <button
        ref={ref}
        className={cn(
          "flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
          className
        )}
        {...props}
        data-state={
          // This requires checking context, which is tricky inside a forwardRef without passing the value down.
          // We will do a quick Context check if we can get the value from somewhere.
          // For now, let's rely on the parent Item to clone children or just Context.
          "closed" // Dynamic update handled in Wrapper
        }
      >
        {children}
        <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
      </button>
    </div>
  )
})
AccordionTrigger.displayName = "AccordionTrigger"

// Wrapper to inject state into Trigger and Content
const AccordionItemWrapper = ({ children, value, className, ...props }: any) => {
  const { expanded, setExpanded } = React.useContext(AccordionContext);
  const isOpen = expanded === value;

  return (
    <div className={cn("border-b", className)} data-value={value} {...props}>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
             const element = child as React.ReactElement<any>;

             if (element.type === AccordionTrigger) {
                return React.cloneElement(element, {
                  "data-state": isOpen ? "open" : "closed",
                  onClick: () => setExpanded(isOpen ? undefined : value)
                });
             }

             if (element.type === AccordionContent) {
                return React.cloneElement(element, { "data-state": isOpen ? "open" : "closed" });
             }
        }
        return child;
      })}
    </div>
  )
}

const AccordionContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
      className
    )}
    {...props}
  >
    <div className="pb-4 pt-0">{children}</div>
  </div>
))
AccordionContent.displayName = "AccordionContent"

export { Accordion, AccordionItemWrapper as AccordionItem, AccordionTrigger, AccordionContent }
